import React, { useState } from 'react';
import { exportApi } from '../../services/api';
import './ExportImport.css';

export default function ExportImport() {
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const handleExportProfile = async () => {
        try {
            setLoading(true);
            setError(null);
            setMessage(null);

            const blob = await exportApi.exportProfile();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `profile-export-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setMessage('تم تصدير الملف الشخصي بنجاح!');
        } catch (err) {
            setError('فشل تصدير الملف الشخصي');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.json')) {
            setError('يرجى اختيار ملف JSON صالح');
            return;
        }

        setImportFile(file);
        setError(null);

        // Read file for preview
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = JSON.parse(event.target.result);
                setImportPreview(data);
            } catch (err) {
                setError('تنسيق ملف JSON غير صالح');
                setImportFile(null);
            }
        };
        reader.readAsText(file);
    };

    const handleImportProfile = async () => {
        if (!importFile) {
            setError('يرجى اختيار ملف للاستيراد');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setMessage(null);

            const response = await exportApi.importProfile(importFile);
            setMessage(`تم استيراد الملف الشخصي بنجاح! تم استيراد ${response.data.imported_count || 0} عناصر.`);
            setImportFile(null);
            setImportPreview(null);

            // Clear file input
            const fileInput = document.getElementById('import-file-input');
            if (fileInput) fileInput.value = '';
        } catch (err) {
            setError(err.message || 'فشل استيراد الملف الشخصي');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelImport = () => {
        setImportFile(null);
        setImportPreview(null);
        setError(null);
        setMessage(null);

        const fileInput = document.getElementById('import-file-input');
        if (fileInput) fileInput.value = '';
    };

    // ترجمة أسماء الأقسام
    const sectionLabels = {
        certifications: 'الشهادات',
        experience: 'الخبرة',
        projects: 'المشاريع',
        education: 'التعليم',
        skills: 'المهارات',
        awards: 'الجوائز',
        languages: 'اللغات',
        personalInfo: 'المعلومات الشخصية',
        industryExperience: 'خبرة القطاع'
    };

    const countItems = (importData) => {
        if (!importData) return 0;
        // Check if data is nested under 'data' key (export format)
        const data = importData.data || importData;
        let count = 0;
        ['certifications', 'experience', 'projects', 'education', 'skills', 'awards', 'languages'].forEach(key => {
            if (data[key] && Array.isArray(data[key])) {
                count += data[key].length;
            }
        });
        return count;
    };

    return (
        <div className="export-import-container">
            <div className="export-import-header">
                <h2 className="export-import-title">تصدير واستيراد الملف الشخصي</h2>
                <p className="export-import-subtitle">
                    احفظ نسخة احتياطية من بياناتك أو انقلها إلى نظام آخر
                </p>
            </div>

            {message && (
                <div className="success-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    {message}
                </div>
            )}

            {error && (
                <div className="error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                </div>
            )}

            <div className="export-import-sections">
                {/* Export Section */}
                <div className="section-card">
                    <div className="section-icon export">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                    </div>
                    <h3 className="section-title">تصدير الملف الشخصي</h3>
                    <p className="section-description">
                        قم بتحميل بيانات ملفك الشخصي الكاملة كملف JSON.
                        يشمل ذلك جميع الشهادات والخبرات والمشاريع والتعليم والمهارات والجوائز واللغات.
                    </p>
                    <div className="section-features">
                        <div className="feature-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>نسخة احتياطية كاملة للملف الشخصي</span>
                        </div>
                        <div className="feature-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>تنسيق JSON للتوافق</span>
                        </div>
                        <div className="feature-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>سهولة نقل البيانات</span>
                        </div>
                    </div>
                    <button
                        className="export-button"
                        onClick={handleExportProfile}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <div className="spinner"></div>
                                جاري التصدير...
                            </>
                        ) : (
                            <>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                تصدير الملف الشخصي
                            </>
                        )}
                    </button>
                </div>

                {/* Import Section */}
                <div className="section-card">
                    <div className="section-icon import">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    </div>
                    <h3 className="section-title">استيراد الملف الشخصي</h3>
                    <p className="section-description">
                        قم برفع ملف JSON تم تصديره مسبقاً لاستعادة بيانات ملفك الشخصي.
                        سيتم دمجها مع البيانات الحالية.
                    </p>
                    <div className="section-features">
                        <div className="feature-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>استعادة سريعة للملف الشخصي</span>
                        </div>
                        <div className="feature-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>معاينة قبل الاستيراد</span>
                        </div>
                        <div className="feature-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            <span>دمج مع البيانات الحالية</span>
                        </div>
                    </div>

                    <div className="file-upload-area">
                        <input
                            id="import-file-input"
                            type="file"
                            accept=".json"
                            onChange={handleFileSelect}
                            className="file-input"
                        />
                        <label htmlFor="import-file-input" className="file-label">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <span>{importFile ? importFile.name : 'اختر ملف JSON'}</span>
                        </label>
                    </div>

                    {importPreview && (
                        <div className="import-preview">
                            <h4 className="preview-subtitle">معاينة الاستيراد</h4>
                            <div className="preview-stats">
                                <div className="preview-stat">
                                    <span className="preview-stat-label">إجمالي العناصر</span>
                                    <span className="preview-stat-value">{countItems(importPreview)}</span>
                                </div>
                                {(importPreview.data?.personalInfo || importPreview.personalInfo) && (
                                    <div className="preview-stat">
                                        <span className="preview-stat-label">المعلومات الشخصية</span>
                                        <span className="preview-stat-value">
                                            {(importPreview.data?.personalInfo?.name || importPreview.personalInfo?.name) || 'متضمنة'}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="preview-sections">
                                {Object.entries(importPreview.data || importPreview).map(([key, value]) => {
                                    if (Array.isArray(value) && value.length > 0) {
                                        return (
                                            <div key={key} className="preview-section-item">
                                                <span className="preview-section-name">
                                                    {sectionLabels[key] || key}
                                                </span>
                                                <span className="preview-section-count">{value.length}</span>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>
                    )}

                    <div className="import-actions">
                        {importFile && (
                            <button
                                className="cancel-button"
                                onClick={handleCancelImport}
                                disabled={loading}
                            >
                                إلغاء
                            </button>
                        )}
                        <button
                            className="import-button"
                            onClick={handleImportProfile}
                            disabled={!importFile || loading}
                        >
                            {loading ? (
                                <>
                                    <div className="spinner"></div>
                                    جاري الاستيراد...
                                </>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    استيراد الملف الشخصي
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
